import {
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { Account } from "../../shared/types/account.type";
import { Credential } from "../../shared/types/credential.type";
import { ActivatedRoute, Router } from "@angular/router";
import { AccountService } from "../../shared/services/account.service";
import { AppTranslateService } from "../../shared/services/app-translate.service";
import { INVALID_PASSWORDS } from "app/shared/constants/invalid-passwords.constant";
import { PasswordConfirmationErrorMatcher } from "app/shared/error-matchers/password-confirmation.matcher";
import { ErrorStateMatcher } from "@angular/material/core";
import { ErrorReportingService } from "app/shared/services/error-reporting.service";
import { TranslateService } from "@ngx-translate/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Title } from "@angular/platform-browser";
import { NgForm, NgModel } from "@angular/forms";
import {
  ProvisioningProfile,
  ProvisioningService,
} from "app/shared/services/provisioning.service";
import { PasswordComplexity, SupportedLanguage } from "@cariloop/ui-components";
import { startWith, switchMap } from "rxjs/operators";
import { combineLatest } from "rxjs";

@Component({
  selector: "complete-register",
  templateUrl: "./complete-register.html",
  styleUrls: ["./complete-register.scss"],
  standalone: false,
})
export class CompleteRegisterComponent implements OnInit, OnDestroy {
  public model: Account = new Account();
  public credential: Credential = new Credential();
  public errorMatcher: ErrorStateMatcher;
  public code: string;
  public error: any;
  public isLoading = false;
  public termsAccepted = false;
  public showMobileWebCTA = false;
  public passwordComplexity: PasswordComplexity;
  public provisioningProfile: ProvisioningProfile;
  public showMobileAdoptionCTA = false;
  @ViewChild("firstNameRef") firstNameControl: NgModel;
  @ViewChild("lastNameRef") lastNameControl: NgModel;

  constructor(
    private route: ActivatedRoute,
    private accountService: AccountService,
    private translate: AppTranslateService,
    private errorReportingService: ErrorReportingService,
    private router: Router,
    private translateService: TranslateService,
    private titleService: Title,
    private destroyRef: DestroyRef,
    private provisioningService: ProvisioningService,
  ) {
    this.errorMatcher = new PasswordConfirmationErrorMatcher();
  }

  public ngOnInit() {
    this.route.data.subscribe(({ account }) => {
      this.model = account;
      this.setUserLanguage(this.model.language as SupportedLanguage);
    });
    this.translateService
      .stream("COMPLETE_REGISTRATION.TITLE")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pageTitle) => {
        this.titleService.setTitle(pageTitle + " | Cariloop");
      });
    this.route.params.subscribe((params) => {
      this.code = params["code"];
    });
    this.provisioningService
      .getProfileGeneral()
      .pipe(
        switchMap((profile) => {
          this.provisioningProfile = profile;
          this.showMobileAdoptionCTA = profile.mobileAdoptionCTA.value;
          this.passwordComplexity = {
            ...profile.passwordComplexity.value,
          };
          return combineLatest([
            this.firstNameControl.valueChanges.pipe(startWith("")),
            this.lastNameControl.valueChanges.pipe(startWith("")),
          ]);
        }),
      )
      .subscribe(([firstName, lastName]) => {
        this.passwordComplexity = {
          ...this.passwordComplexity,
          containsNameOrEmail: {
            firstName,
            lastName,
            email: this.model.email,
          },
        };
      });
  }

  ngOnDestroy() {
    this.titleService.setTitle("Cariloop");
  }

  public onSubmit(ngForm: NgForm): void {
    if (ngForm.invalid) {
      ngForm.form.markAllAsTouched();
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { provisioningProfile, orgHistory, ...model } = this.model;
    this.isLoading = true;
    this.error = "";
    model.email = model.email?.trim() ?? "";
    model.invited = false;
    model.state = "active";
    model.termsAcceptanceDate = new Date().toISOString();
    this.accountService.completeRegisterByCode(this.code, model).subscribe(
      () => this.doLogin(),
      (err) => this.errorHandler(err, ngForm),
    );
  }

  public doLogin() {
    this.credential = {
      email: this.model.email,
      password: this.model.password,
    };
    this.accountService.login(this.credential).subscribe(() => {
      window.location.href = this.accountService.getHomeUrl();
    });
  }

  private setUserLanguage(lang = SupportedLanguage.EN): void {
    this.translate.updateLang(lang);
  }

  private errorHandler(err: any, ngForm: NgForm): void {
    this.isLoading = false;
    if (
      err?.error?.error?.message !== "Invalid Password" &&
      err?.error?.error?.message !==
        "Password is too common or has been reported as compromised"
    ) {
      this.errorReportingService.send(err);
    } else {
      ngForm.form.controls["password"].setErrors({ knownBadPassword: true });
      return;
    }
    this.error = err;
  }
}
